import React, { useCallback, useState } from 'react'
import { FormGroup, Label, Input, Row, Col } from 'reactstrap'
import Resx from '@/components/Resx'
import {
  BsrsGrid,
  DateCell,
  DateFilterCell,
  NameColumn,
  commandDeleteColumnProps,
  commandColumnProps
} from '@/components/BsrsGrid'
import { GridColumn as Column } from '@progress/kendo-react-grid'
import context from '@/context'
import { fromUtcDate } from '@/utils/dateUtilities'
import { ServerValidator } from '@/components/validation'
import { propertyToVerify as properties } from '@/enums/PropertyToVerify'
import { useLocalization } from '@/hooks/localization'
import { useDialog } from '@/hooks/useDialog'
import { dialogTypes } from '@/dialogs'

function InstitutionGroupDetail (props) {
  const { members, disabled, onMembersChange, name, latinName, id, onNameChange, onLatinNameChange } = props
  const openDialog = useDialog()
  const { localize, locale } = useLocalization()
  const [lastReloaded, setLastReloaded] = useState(new Date())

  function addToGroup (institution, validityDates = {}) {
    if (members.find(x => x.institutionId === institution.id)) return

    const newMember = { institutionId: institution.id, institutionName: institution.name, institutionLatinName: institution.latinName, ...validityDates }
    onMembersChange([...props.members, newMember])
    setLastReloaded(new Date())
  }

  function deleteGroup (id) {
    onMembersChange(members.filter(x => x.institutionId !== id))
    setLastReloaded(new Date())
  }

  const memberIds = (members.map(x => x.institutionId) || []).toString()

  const getFilteredLookup = useCallback((request) =>
    context.api.institutionService.getLookupExcluding(request, memberIds), [memberIds])

  const selectedInstitutions = members.map(item => ({
    id: item.institutionId,
    name: item.institutionName,
    latinName: item.institutionLatinName,
    validFrom: fromUtcDate(item.validFrom),
    validTo: fromUtcDate(item.validTo)
  }))

  return (
    <React.Fragment>
      <Row>
        <Col>
          <FormGroup>
            <Label><Resx name='Common.ArabicName' /></Label>
            <ServerValidator
              name='name'
              value={name}
              required
              serverValidation={(value) => context.api.institutionGroupService.validatePropertyValueIsUnique({ value, id, propertyToVerify: properties.enum.Name })}>
              <Input disabled={disabled} value={name} onChange={(e) => onNameChange(e.target.value)} />
            </ServerValidator>
          </FormGroup>
        </Col>
        <Col>
          <FormGroup>
            <Label><Resx name='Common.LatinName' /></Label>
            <ServerValidator
              name='latinName'
              value={latinName}
              serverValidation={(value) => context.api.institutionGroupService.validatePropertyValueIsUnique({ value, id, propertyToVerify: properties.enum.LatinName })}>
              <Input disabled={disabled} value={latinName} onChange={(e) => onLatinNameChange(e.target.value)} />
            </ServerValidator>
          </FormGroup>
        </Col>
      </Row>
      <FormGroup>
        <Label><Resx name='Institutions.SelectedInstitutions' /></Label>
        <BsrsGrid items={selectedInstitutions} pageSize={5} disableExcelExport>
          <Column field='id' title={localize('Common.Id')} filter='numeric' width='150px' editable={false} />
          {NameColumn({ locale, title: localize('Common.Name'), editable: false })}
          <Column field='validFrom' title={localize('Common.ValidFrom')} filter='date' cell={DateCell} filterCell={DateFilterCell} />
          <Column field='validTo' title={localize('Common.ValidTo')} filter='date' cell={DateCell} filterCell={DateFilterCell} />
          <Column {...commandDeleteColumnProps(deleteGroup, { disabled })} />
        </BsrsGrid>
      </FormGroup>

      {!disabled && <React.Fragment>
        <hr />
        <Label><Resx name='Institutions.SelectInstitutions' /></Label>
        <BsrsGrid dataSource={getFilteredLookup} lastReloadedAt={lastReloaded} pageSize={5} disableExcelExport>
          {NameColumn({ locale, title: localize('Common.Name'), editable: false })}
          <Column field='code' title={localize('Common.Code')} width='150px' editable={false} />
          <Column {...commandColumnProps({
            additionalButtons: [
              { icon: 'plus', title: localize('Common.Add'), onClick: x => addToGroup(x) },
              { icon: 'calendar-plus',
                title: localize('Common.AddWithValidRange'),
                onClick: x => openDialog({
                  dialogType: dialogTypes.setValidRange,
                  onAction: (validityDates) => addToGroup(x, validityDates)
                }) }
            ]
          })} width={125} />
        </BsrsGrid>
      </React.Fragment>}

    </React.Fragment>
  )
}

export { InstitutionGroupDetail }



// WEBPACK FOOTER //
// ./src/internal-app/pages/institutionGroups/detail.js